import React, { useMemo, useState, useCallback, useEffect } from "react";
import Styles from "./ConnectionModal.module.css";
import { Modal } from "@mui/material";
import { Close } from "@mui/icons-material";
import { ReactComponent as RequisitionLeft } from "../../../../assets/images/RequisitionLeft.svg";
import { ReactComponent as RequisitionRight } from "../../../../assets/images/RequisitionRight.svg";
import { useDispatch, useSelector } from "react-redux";
import { acceptConnection } from "../../../../Services/BuySell/Creation";
import { IconButton, Tooltip } from "@mui/material";
import { ReactComponent as InfoIcon } from "../../../../assets/images/info.svg";
import { useLocation } from "react-router-dom";
import {
  fetchCounterPartList,
  fetchLedgerBids,
  fetchSelfCapacityById,
} from "../../../../Redux/Slices/CapacitySlice";
import { formatDate, formatSlotTime } from "../../../../helpers/dateFormat";
import LaunchIcon from "@mui/icons-material/Launch";
import { usePdfPreview } from "../../../../hooks/usePdfPreview";
import ConfirmationModal from "../../../../commons/ConfirmationModal/ConfirmationModal";
import ActiveConnection from "../ActiveConnection/ActiveConnection";
import { toast } from "react-toastify";
import DamStrip from "./DamStrip";
import SocketService from "../../../../commons/Socket/SocketService";

const ConnectionModal = ({
  open,
  handleClose,
  connectionText,
  activeId,
  wholeData,
}) => {
  const isOpen = Boolean(open);
  const location = useLocation();
  const listingId = useMemo(
    () => activeId || new URLSearchParams(location.search).get("id"),
    [activeId, location.search],
  );
  const dispatch = useDispatch();
  const user = useSelector(
    (state) => state.persistedReducer.user?.activeRole,
  )?.toLowerCase();

  useEffect(() => {
    dispatch(fetchSelfCapacityById({ user, id: listingId }));
  }, [dispatch, listingId, user]);

  const currentListing = useSelector(
    (store) => store?.CapacitySlice?.getSelfListingById,
  );

  const selfDataName = useSelector(
    (state) => state?.CapacitySlice?.activeMain?.name,
  );

  const [viewMore, setViewMore] = useState(false);
  const [activeConnectionModal, setActiveConnectionModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [confirmAcceptModal, setConfirmAcceptModal] = useState(false);
  const [confirmDeclineModal, setConfirmDeclineModal] = useState(false);

  const selfListingName = useSelector(
    (state) => state?.CapacitySlice?.activeMain,
  );
  useEffect(() => {
    dispatch(fetchLedgerBids(listingId));
  }, [dispatch, listingId]);

  // Derived memoized data
  const mainData = useMemo(() => open?.matches || [], [open]);

  // Find the first index where generators or consumers exist
  const initialReqIndex = useMemo(() => {
    return mainData.findIndex(
      (item) => item.generators?.length > 0 || item.consumers?.length > 0,
    );
  }, [mainData]);

  const [currReq, setCurrReq] = useState(
    initialReqIndex >= 0 ? initialReqIndex : 0,
  );

  // If open changes, reset currReq to the correct index
  React.useEffect(() => {
    setCurrReq(initialReqIndex >= 0 ? initialReqIndex : 0);
  }, [initialReqIndex]);

  const currentMatch = useMemo(
    () => mainData?.[currReq] || {},
    [mainData, currReq],
  );

  const selfData = currentMatch?.selfSlot || {};

  const counterList = useMemo(() => {
    if (!user) return [];
    return user === "generator"
      ? currentMatch?.consumers || []
      : currentMatch?.generators || [];
  }, [user, currentMatch]);


  const counterFirstSlotDetails = open?.generator || open?.consumer || {};
  // UI helpers
  const formatRange = (start, end) => [start || "-", end || "-"].join(" - ");

  const numberOrDash = (val, suffix = "") =>
    val === null || val === undefined || val === "" ? "-" : `${val}${suffix}`;

  // Navigation for requisitions (wrap-around)
  const handleReq = useCallback(
    (dir) => {
      if (!mainData?.length) return;
      if (dir === "up") {
        setCurrReq((i) => (i + 1) % mainData.length);
      } else if (dir === "down") {
        setCurrReq((i) => (i - 1 + mainData.length) % mainData.length);
      }
    },
    [mainData],
  );

  const handleChatSetup = (fromName, toName) => {
    const socket = SocketService.getSocket();
    let chatId;

    if (user === "consumer") {
      chatId = `${fromName?.split("_")?.[0]}_${toName?.split("_")?.[0]}`;
    } else {
      chatId = `${toName?.split("_")?.[0]}_${fromName?.split("_")?.[0]}`;
    }
    socket.emit("create-room", {
      chatId,
      fromUsername: fromName,
      toUsername: toName,
    });
    socket.emit("join-room", { chatId, username: fromName });
  };

  const handleAccept = useCallback(async () => {
    try {
      setSubmitting(true);
      const payload = { status: "APPROVED", reason: "PRICE" };
      const res = await acceptConnection(payload, open?.id);
      if (res?.data?.code === 200) {
        try {
          handleChatSetup(
            selfDataName,
            open?.consumer?.name || open?.generator?.name,
          );
        } catch (error) {
          console.error("Error setting up chat:", error);
        }

        dispatch(fetchLedgerBids(listingId));
        const payload = {
          id: listingId,
          energySourceType: "",
          energyFuelType: "",
          tag: "",
          // matchDisabled: true,
        };
        dispatch(fetchCounterPartList({ user, payload, curr: 0 }));
        handleClose();
        toast.success("EMD blocked successfully");
        // setActiveConnectionModal(true);
      }
    } catch (error) {
      toast.error("Error accepting connection");
    } finally {
      setSubmitting(false);
      setConfirmAcceptModal(false);
    }
  }, [open?.id, dispatch, listingId, user, handleClose]);

  const handleDecline = useCallback(async () => {
    try {
      setDeclining(true);
      const payload = { status: "REJECTED", reason: "PRICE" };
      const res = await acceptConnection(payload, open?.id);
      if (res?.data?.code === 200) {
        dispatch(fetchLedgerBids(listingId));
        const payload = {
          id: listingId,
          energySourceType: "",
          energyFuelType: "",
          tag: "",
          // matchDisabled: true,
        };
        dispatch(fetchCounterPartList({ user, payload, curr: 0 }));
        handleClose();
      }
    } finally {
      setDeclining(false);
      setConfirmDeclineModal(false);
    }
  }, [open?.id, dispatch, listingId, user, handleClose]);

  const { handlePreviewGeneralTerms, PdfPreviewModal } = usePdfPreview();

  return (
    <>
      <ConfirmationModal
        open={confirmAcceptModal}
        handleClose={() => setConfirmAcceptModal(false)}
        handleSubmit={() => handleAccept()}
        header={"Accept Connection Confirmation"}
        data={
          <>
            By accepting this connection request, your details will be shared
            with the counterparty and{" "}
            <strong>the required EMD/security deposit will be blocked</strong>.
            <br />
            Do you want to continue?
          </>
        }
        loading={submitting}
      />
      <ConfirmationModal
        open={confirmDeclineModal}
        handleClose={() => setConfirmDeclineModal(false)}
        handleSubmit={() => handleDecline()}
        header={"Decline Connection Confirmation"}
        data={
          "Are you sure you want to decline this request? This action cannot be reversed."
        }
        loading={declining}
      />

      <ActiveConnection
        open={activeConnectionModal}
        onClose={() => setActiveConnectionModal(false)}
      />
      <PdfPreviewModal />
      <Modal
        open={isOpen}
        onClose={handleClose}
        aria-labelledby="connection-modal-title"
        aria-describedby="connection-modal-description"
      >
        <div className={`popupModal ${Styles.connectionMainContainer}`}>
          <div className="modalHeading">
            <div className="headingText">{connectionText}</div>
            <Close
              onClick={handleClose}
              style={{ cursor: "pointer", fontSize: "18px" }}
              alterButton={false}
            />
          </div>

          <DamStrip />

          {mainData?.length > 1 && (
            <div className={`display_flex_row ${Styles.req_switchContainer}`}>
              <div
                onClick={() => handleReq("down")}
                style={{ borderRight: "1px solid #e4e7ed" }}
                className={`display_flex_row_center ${Styles.req_switchText}`}
              >
                <RequisitionRight className={Styles.req_switchIcon} />
              </div>
              <div className={`${Styles.req_mainContainer}`}>
                {mainData.map((item, index) => {
                  if (item.generators || item.consumers) {
                    return (
                      <div
                        key={`req-${index}`}
                        className={
                          currReq === index
                            ? Styles.req_textContainer_active
                            : Styles.req_textContainer
                        }
                        onClick={() => setCurrReq(index)}
                      >
                        Requisition {index + 1}
                      </div>
                    );
                  } else {
                    return null;
                  }
                })}
              </div>
              <div
                style={{ borderLeft: "1px solid #e4e7ed" }}
                onClick={() => handleReq("up")}
                className={`display_flex_row_center ${Styles.req_switchText}`}
              >
                <RequisitionLeft className={Styles.req_switchIcon} />
              </div>
            </div>
          )}

          <div className={`scroll_bar_y ${Styles.viewMainContainer}`}>
            <div className={Styles.listingMainContainer}>
              <div className={`display_flex_row ${Styles.listingHeader}`}>
                <div style={{ width: "20%" }}>Requisition</div>
                <div style={{ width: "15%" }}>Quantity</div>
                <div style={{ width: "15%" }}>Energy Rate</div>
                <div style={{ width: "20%" }}>Date Range</div>
                <div style={{ width: "15%" }}>Time Range</div>
              </div>

              <div>
                <div className={`display_flex_row ${Styles.requisitionMain}`}>
                  <div style={{ width: "20%" }}>
                    {`Self Requisition ${currReq + 1}` || "-"}
                  </div>
                  <div style={{ width: "15%" }}>
                    {numberOrDash(
                      selfData?.purchaseEnergy ?? selfData?.sellEnergy,
                      " (MW)",
                    )}
                  </div>
                  <div style={{ width: "15%" }}>
                    {selfData?.energyRateStr ||
                      numberOrDash(
                        selfData?.landingCost ?? selfData?.sellingCost,
                      )}{" "}
                    {selfData?.energyRateType === "FIXED" && "(₹/kWh)"}
                  </div>
                  <div style={{ width: "20%" }}>
                    {formatDate(selfData?.dateStartAt)} -{" "}
                    {formatDate(selfData?.dateEndAt)}
                  </div>
                  <div style={{ width: "15%" }}>
                    {formatRange(
                      formatSlotTime(selfData?.slotStartAt),
                      formatSlotTime(selfData?.slotEndAt),
                    )}
                  </div>
                </div>

                <div className={`scroll_bar_y ${Styles.listingContainer}`}>
                  {counterList?.map((ele, index) => {
                    const s = ele?.selfSlot || {};
                    return (
                      <div
                        className={Styles.listingMain_details}
                        key={ele?.id ?? `ctr-${index}`}
                      >
                        {index === 0 && (
                          <div
                            style={{
                              position: "relative",
                              backgroundColor: "#EFFCFF",
                              borderRadius: "10px 10px 0 0",
                            }}
                            className={`display_flex_row ${Styles.listingCompName}`}
                          >
                            <div
                              style={{ width: "20%", marginLeft: "30px" }}
                              className={`display_flex_row_gap_8`}
                            >
                              <div>
                                <div>
                                  {ele?.name || open?.consumer?.name || "-"}
                                </div>
                              </div>
                            </div>

                            <span
                              style={{
                                color: "#34656D",
                                fontSize: "10px",
                                fontWeight: 500,
                                position: "absolute",
                                left: "37px",
                                top: "-8px",
                                backgroundColor: "white",
                              }}
                            >
                              Counterparty Name
                            </span>
                          </div>
                        )}
                        <div
                          className={`display_flex_row ${Styles.listingDetails}`}
                        >
                          <div
                            style={{ width: "20%", textAlign: "left" }}
                          >{`Requisition ${index + 1}`}</div>
                          <div style={{ width: "15%" }}>
                            {numberOrDash(
                              s?.purchaseEnergy ?? s?.sellEnergy,
                              " (MW)",
                            )}
                          </div>
                          <div style={{ width: "15%" }}>
                            {s?.energyRateStr ||
                              numberOrDash(
                                s?.landingCost ?? s?.sellingCost,
                              )}{" "}
                            {s?.energyRateType === "FIXED" && "(₹/kWh)"}
                          </div>
                          <div style={{ width: "20%", textWrap: "nowrap" }}>
                            {formatDate(s?.dateStartAt)} -{" "}
                            {formatDate(s?.dateEndAt)}
                          </div>
                          <div style={{ width: "15%" }}>
                            {formatRange(
                              formatSlotTime(s?.slotStartAt),
                              formatSlotTime(s?.slotEndAt),
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {viewMore && (
                  <div className={Styles.viewMore_elem}>
                    <div
                      className={`display_flex_row ${Styles.detailsMainCont}`}
                    >
                      <div
                        style={{
                          width: "25%",
                          paddingLeft: "50px",
                          textAlign: "left",
                        }}
                      >
                        Category
                      </div>
                      <div
                        style={{
                          width: "30%",
                        }}
                      >
                        My Listing
                      </div>
                      <div
                        style={{
                          width: "30%",
                        }}
                      >
                        Counterparty Listing
                      </div>
                    </div>

                    {/* <div
                      className={`display_flex_row ${Styles.detailsMainCont_element}`}
                    >
                      <div
                        style={{
                          width: "25%",
                          paddingLeft: "50px",
                          textAlign: "left",
                        }}
                      >
                        Initial Margin{" "}
                        <Tooltip
                          title={
                            <span style={{ whiteSpace: "pre-line" }}>
                              <strong>Buyer’s Initial Margin</strong>: The
                              percentage of the total transaction value required
                              to be deposited by the Buyer for a single day.
                              <br />
                              <br />
                              <strong>Seller’s Initial Margin</strong>: A fixed
                              margin equivalent to 20% of the total transaction
                              value, to be deposited by the Seller for a single
                              day.
                            </span>
                          }
                        >
                          <IconButton>
                            <InfoIcon />
                          </IconButton>
                        </Tooltip>
                      </div>
                      <div style={{ width: "30%" }}>
                        {numberOrDash(currentMatch?.initialMargin)} %
                      </div>
                      <div style={{ width: "30%" }}>
                        {numberOrDash(counterFirstSlotDetails?.initialMargin)} %
                      </div>
                    </div>

                    <div
                      className={`display_flex_row ${Styles.detailsMainCont_element}`}
                    >
                      <div
                        style={{
                          width: "25%",
                          paddingLeft: "50px",
                          textAlign: "left",
                        }}
                      >
                        EMD
                        <Tooltip
                          title={
                            <span style={{ whiteSpace: "pre-line" }}>
                              <strong>Seller’s EMD:</strong> An amount of
                              ₹30,000 per MW or ₹1,00,000, whichever is lower,
                              shall be deducted.
                              <br />
                              <br />
                              <strong>Buyer's EMD:</strong> 0
                            </span>
                          }
                        >
                          <IconButton>
                            <InfoIcon />
                          </IconButton>
                        </Tooltip>
                      </div>
                      <div style={{ width: "30%" }}>
                        {numberOrDash(currentMatch?.emd)} (₹/MW)
                      </div>
                      <div style={{ width: "30%" }}>
                        {numberOrDash(counterFirstSlotDetails?.emd)} (₹/MW)
                      </div>
                    </div>

                    <div
                      className={`display_flex_row ${Styles.detailsMainCont_element}`}
                    >
                      <div
                        style={{
                          width: "25%",
                          paddingLeft: "50px",
                          textAlign: "left",
                        }}
                      >
                        Payment Security
                        <Tooltip
                          title={
                            <span style={{ whiteSpace: "pre-line" }}>
                              <strong>Buyer’s Payment Security:</strong>{" "}
                              Calculated as the Buyer’s Initial Margin
                              multiplied by the transaction value for one
                              trading day and the number of days of payment
                              security.
                              <br />
                              <br />
                              <strong>Seller’s Payment Security:</strong>{" "}
                              Sellers’ initial margin will be considered as
                              payment security.
                            </span>
                          }
                        >
                          <IconButton>
                            <InfoIcon />
                          </IconButton>
                        </Tooltip>
                      </div>
                      <div style={{ width: "30%" }}>
                        {numberOrDash(currentMatch?.paymentSecurity)} (Days)
                      </div>
                      <div style={{ width: "30%" }}>
                        {numberOrDash(counterFirstSlotDetails?.paymentSecurity)}{" "}
                        (Days)
                      </div>
                    </div> */}

                    <div
                      className={`display_flex_row ${Styles.detailsMainCont_element}`}
                    >
                      <div
                        style={{
                          width: "25%",
                          paddingLeft: "50px",
                          textAlign: "left",
                        }}
                      >
                        Listing Expiry
                      </div>
                      <div style={{ width: "30%" }}>
                        {formatDate(currentListing?.listingExpDate) || "-"}
                        {" - "}
                        {currentListing?.listingExpTime || ""}
                      </div>
                      <div style={{ width: "30%" }}>
                        {formatDate(counterFirstSlotDetails?.listingExpDate) ||
                          "-"}
                        {" - "}
                        {formatSlotTime(
                          counterFirstSlotDetails?.listingExpTime,
                        ) || ""}
                      </div>
                    </div>

                    {/* <div
                    className={`display_flex_row ${Styles.detailsMainCont_element}`}
                  >
                    <div
                      style={{
                        width: "25%",
                        paddingLeft: "50px",
                        textAlign: "left",
                      }}
                    >
                      Terms Expiry
                    </div>
                    <div style={{ width: "30%" }}>
                      {formatDate(currentListing?.listingExpDate) || "-"}
                      {" - "}
                      {currentListing?.listingExpTime || ""}
                    </div>
                    <div style={{ width: "30%" }}>
                      {formatDate(counterFirstSlotDetails?.listingExpDate) ||
                        "-"}
                      {" - "}
                      {formatSlotTime(
                        counterFirstSlotDetails?.listingExpTime
                      ) || ""}
                    </div>
                  </div> */}

                    <div
                      className={`display_flex_row ${Styles.detailsMainCont_element}`}
                    >
                      <div
                        style={{
                          width: "25%",
                          paddingLeft: "50px",
                          textAlign: "left",
                        }}
                      >
                        System T&C
                      </div>
                      <div
                        onClick={() =>
                          handlePreviewGeneralTerms(
                            "https://gna-general-files.s3.ap-south-1.amazonaws.com/Website+Assets+PDF/GNA-bye-laws.pdf",
                          )
                        }
                        style={{
                          width: "30%",
                          color: "var(--primary-color-text)",
                          cursor: "pointer",
                        }}
                      >
                        View
                      </div>
                      <div
                        onClick={() =>
                          handlePreviewGeneralTerms(
                            "https://gna-general-files.s3.ap-south-1.amazonaws.com/Website+Assets+PDF/GNA-bye-laws.pdf",
                          )
                        }
                        style={{
                          width: "30%",
                          color: "var(--primary-color-text)",
                          cursor: "pointer",
                        }}
                      >
                        View
                      </div>
                    </div>
                    {counterFirstSlotDetails?.termsDocumentId && (
                      <div
                        className={`display_flex_row ${Styles.detailsMainCont_element}`}
                      >
                        <div
                          style={{
                            width: "25%",
                            paddingLeft: "50px",
                            textAlign: "left",
                          }}
                        >
                          General T&C
                        </div>
                        <div
                          onClick={() =>
                            handlePreviewGeneralTerms(
                              currentListing?.termsDocumentId,
                            )
                          }
                          style={{
                            width: "30%",
                            color: "var(--primary-color-text)",
                            cursor: "pointer",
                          }}
                        >
                          View
                        </div>
                        <div
                          onClick={() =>
                            handlePreviewGeneralTerms(
                              counterFirstSlotDetails?.termsDocumentId,
                            )
                          }
                          style={{
                            width: "30%",
                            color: "var(--primary-color-text)",
                            cursor: "pointer",
                          }}
                        >
                          View
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div
                  onClick={() => setViewMore((v) => !v)}
                  className={`display_flex_row_center ${Styles.viewMore}`}
                >
                  View {viewMore ? "Less" : "More"}
                </div>

                {/* {!viewMore && (
                  <div className={` ${Styles.terms}`}>
                    <p className={Styles.terms_title}>Terms and Conditions</p>
                    <p
                      onClick={() =>
                        handlePreviewGeneralTerms(
                          "https://gna-general-files.s3.ap-south-1.amazonaws.com/Website+Assets+PDF/GNA-bye-laws.pdf"
                        )
                      }
                      className={Styles.terms_item}
                    >
                      System <LaunchIcon fontSize="10px" />
                    </p>
                    <p
                      title={`${
                        counterFirstSlotDetails?.termsDocumentId
                          ? "View Terms & Conditions"
                          : "T&C not available"
                      }`}
                      onClick={() => {
                        if (counterFirstSlotDetails?.termsDocumentId) {
                          handlePreviewGeneralTerms(
                            counterFirstSlotDetails?.termsDocumentId
                          );
                        }
                      }}
                      style={{
                        cursor: counterFirstSlotDetails?.termsDocumentId
                          ? "pointer"
                          : "not-allowed",
                        opacity: counterFirstSlotDetails?.termsDocumentId
                          ? 1
                          : 0.6,
                      }}
                      className={Styles.terms_item}
                    >
                      General <LaunchIcon fontSize="10px" />
                    </p>
                  </div>
                )} */}
              </div>
            </div>
          </div>

          {open?.status === "RECEIVED" && (
            <div
              className={`display_flex_row_center ${Styles.buttonMainContainer}`}
            >
              <div
                className={`delete_button`}
                style={
                  declining
                    ? { pointerEvents: "none", opacity: 0.6 }
                    : undefined
                }
                onClick={
                  declining ? undefined : () => setConfirmDeclineModal(true)
                }
              >
                {declining ? "Declining..." : "Decline"}
              </div>
              <div
                className={`activeButton`}
                style={
                  submitting
                    ? { pointerEvents: "none", opacity: 0.6 }
                    : undefined
                }
                onClick={
                  submitting ? undefined : () => setConfirmAcceptModal(true)
                }
              >
                {submitting ? "Submitting..." : "Accept"}
              </div>
            </div>
          )}
        </div>
      </Modal>
    </>
  );
};

export default ConnectionModal;
